/**
 * The conversation rows in the chat panel's history menu.
 *
 * Extracted from BirdChatPanel because the panel sits at its complexity
 * ceiling: the rename-in-place branch, the empty state and the per-row menu are
 * four conditionals the linter counts against the whole component, and none of
 * them is about the panel.
 *
 * Rename renders a Box rather than a MenuItem on purpose — Chakra's MenuList
 * implements typeahead and would consume the keystrokes as navigation.
 *
 * The per-row options Menu is `isLazy` because the cost is paid per row: without
 * it every conversation builds three MenuItems, a divider and a popper instance
 * on every render of the history menu, and at most one row's menu is ever open.
 * The list is paginated to grow, so that cost scales with "Load more".
 */

import type { RefObject } from 'react';
import {
  Box,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { formatTimeAgo } from '../../../utils/timeUtils';
import type { AgentSessionSummary } from '../../../services/agentChat.service';

interface SessionListProps {
  readonly sessions: readonly AgentSessionSummary[];
  readonly currentSessionId: string;
  readonly renamingSessionId: string | null;
  readonly renameValue: string;
  readonly renameInputRef: RefObject<HTMLInputElement | null>;
  readonly onRenameValueChange: (value: string) => void;
  readonly onRenameSubmit: (sessionId: string) => void;
  readonly onRenameCancel: () => void;
  readonly onRenameStart: (sessionId: string, title: string) => void;
  readonly onSelect: (sessionId: string) => void;
  readonly onCopy: (sessionId: string) => void;
  readonly onDelete: (sessionId: string) => void;
}

export function SessionList(props: SessionListProps) {
  const {
    sessions,
    currentSessionId,
    renamingSessionId,
    renameValue,
    renameInputRef,
    onRenameValueChange,
    onRenameSubmit,
    onRenameCancel,
    onRenameStart,
    onSelect,
    onCopy,
    onDelete,
  } = props;

  return sessions.length === 0 ? (
    <Text fontSize="sm" color="text.muted" px={3} py={2}>
      No previous conversations
    </Text>
  ) : (
    sessions.map((session) =>
      renamingSessionId === session.session_id ? (
        // Editing mode: render Box with Input (not MenuItem)
        <Box
          key={session.session_id}
          px={3}
          py={2}
          bg={session.session_id === currentSessionId ? 'bg.muted' : undefined}
        >
          <HStack spacing={2}>
            <Input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => onRenameValueChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onRenameSubmit(session.session_id);
                } else if (e.key === 'Escape') {
                  onRenameCancel();
                }
              }}
              size="sm"
              fontSize="sm"
              flex={1}
              autoFocus
            />
            <IconButton
              aria-label="Save"
              icon={<MaterialIcon name="check" boxSize="14px" />}
              size="xs"
              bg="brand.primary"
              color="text.onBrand"
              _hover={{ bg: 'brand.hover' }}
              onClick={() => onRenameSubmit(session.session_id)}
              minW="24px"
              h="24px"
            />
            <IconButton
              aria-label="Cancel"
              icon={<MaterialIcon name="close" boxSize="14px" />}
              size="xs"
              variant="ghost"
              color="text.secondary"
              _hover={{ color: 'text.primary' }}
              onClick={onRenameCancel}
              minW="24px"
              h="24px"
            />
          </HStack>
        </Box>
      ) : (
        // Normal mode: render MenuItem
        <MenuItem
          key={session.session_id}
          onClick={() => {
            // The user chose a different conversation, so any pending jump is
            // abandoned. Without this the suppression follows them into the
            // conversation they picked, which then opens scrolled to the top
            // of its transcript instead of at the newest message.
            onSelect(session.session_id);
          }}
          bg={session.session_id === currentSessionId ? 'bg.muted' : undefined}
          fontWeight={session.session_id === currentSessionId ? '600' : 'normal'}
          fontSize="sm"
        >
          <HStack w="full" spacing={2}>
            <Text
              flex={1}
              minW={0}
              noOfLines={1}
              color={session.session_id === currentSessionId ? 'text.primary' : 'text.secondary'}
            >
              {session.title || 'Conversation'}
            </Text>
            <Text fontSize="xs" color="text.muted" flexShrink={0}>
              {formatTimeAgo(new Date(session.last_update_time * 1000))}
            </Text>
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Session options"
                icon={<MaterialIcon name="more_vert" boxSize="14px" />}
                size="xs"
                variant="ghost"
                color="text.muted"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                minW="24px"
                h="24px"
              />
              <MenuList zIndex="popover" onClick={(e) => e.stopPropagation()}>
                <MenuItem
                  icon={<MaterialIcon name="edit" boxSize="14px" />}
                  fontSize="sm"
                  onClick={() => onRenameStart(session.session_id, session.title || 'Conversation')}
                >
                  Rename
                </MenuItem>
                <MenuItem
                  icon={<MaterialIcon name="content_copy" boxSize="14px" />}
                  fontSize="sm"
                  onClick={() => onCopy(session.session_id)}
                >
                  Copy conversation
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  icon={<MaterialIcon name="delete" boxSize="14px" color="text.error" />}
                  fontSize="sm"
                  color="text.error"
                  onClick={() => onDelete(session.session_id)}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </MenuItem>
      ),
    )
  );
}
